import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Text } from "components/base/Form";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AI_ASK_QUESTION, AI_CONVERSATION } from "./graphql";
import MessageView from "./MessageView";

const ConversationPage = () => {
  const { id } = useParams();
  const [question, setQuestion] = useState("");
  const [submitError, setSubmitError] = useState(null);
  const answersContainerRef = useRef(null);
  const askAbortControllerRef = useRef(null);

  const { loading, error, data, refetch } = useQuery(AI_CONVERSATION, {
    variables: { id },
    skip: !id,
    fetchPolicy: "network-only",
  });

  const [ask, askRes] = useMutation(AI_ASK_QUESTION, {
    onCompleted: () => {
      setQuestion("");
      refetch();
    },
    onError: (error) => {
      if (error?.networkError?.name === "AbortError") return;
      setSubmitError(error.message);
    },
  });

  const handleAsk = () => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || askRes.loading || !id) return;
    setSubmitError(null);

    const abortController = new AbortController();
    askAbortControllerRef.current = abortController;

    ask({
      variables: {
        question: trimmedQuestion,
        conversationId: id,
      },
      context: {
        fetchOptions: {
          signal: abortController.signal,
        },
      },
    }).finally(() => {
      if (askAbortControllerRef.current === abortController) {
        askAbortControllerRef.current = null;
      }
    });
  };

  const handleStop = () => {
    askAbortControllerRef.current?.abort();
    askAbortControllerRef.current = null;
  };

  const handleSqlDetailsToggle = (event, isLastMessage) => {
    if (!event.currentTarget.open || !isLastMessage) return;
    requestAnimationFrame(() => {
      const container = answersContainerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
        return;
      }
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    });
  };

  useEffect(() => {
    const messageCount = data?.aiConversation?.messages?.length || 0;
    if (!messageCount || !answersContainerRef.current) return;
    answersContainerRef.current.scrollTo({
      top: answersContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [data?.aiConversation?.messages?.length]);

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const conversation = data?.aiConversation;

  if (!conversation) {
    return (
      <Page title="Conversation" backTo="/ai/ask/history">
        <div className="p-6">Conversation not found.</div>
      </Page>
    );
  }

  return (
    <Page
      className="font-mono leading-relaxed h-screen"
      title={conversation.title || "Conversation"}
      backTo="/ai/ask/history"
    >
      <div className="p-6 flex-1 overflow-auto flex flex-col space-y-8">
        <div className="overflow-auto" ref={answersContainerRef}>
          <div className="divide-y dark:divide-gray-700 px-2">
            {conversation.messages.map((message, index) => (
              <MessageView
                key={message.id}
                message={message}
                isLastMessage={index === conversation.messages.length - 1}
                onSqlDetailsToggle={handleSqlDetailsToggle}
              />
            ))}
          </div>
        </div>

        <div>
          <Text
            placeholder="Ask Anything..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAsk();
              }
            }}
            disabled={askRes.loading}
          />
          {submitError ? (
            <div className="text-red-500 mt-2 text-xs">{submitError}</div>
          ) : null}
          <div className="flex mt-4 space-x-4">
            <Button primary onClick={handleAsk} loading={askRes.loading}>
              {askRes.loading ? "Thinking..." : "Ask Bob"}
            </Button>
            {askRes.loading ? (
              <Button danger onClick={handleStop}>
                Stop
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default ConversationPage;
